import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp, Users, Megaphone, ScanLine, KeyRound, Sparkles, Download,
  Receipt, RefreshCcw, Send, Copy, ShieldCheck, AlertCircle, ExternalLink,
  FileText, BookOpen, UserPlus, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/contexts/AppContext";

const DASHBOARD_TABS = ["stats", "bookings", "reports", "checkins", "ads"];
const asArray = (value) => Array.isArray(value) ? value : [];
const ticketingLabels = {
  namaste: "Namaste ticketing",
  external: "External ticket link",
  inquiry: "Inquiry only",
};
const ACTIVE_BOOKING_STATUSES = ["confirmed", "scanned", "partially_scanned", "partially_refunded"];

function bookingQuantity(booking) {
  const items = asArray(booking.ticket_items);
  if (items.length) return items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  return Number(booking.quantity) || 1;
}

function bookingTicketLabel(booking) {
  const items = asArray(booking.ticket_items).filter((item) => Number(item.quantity) > 0);
  if (!items.length) return booking.category || "General";
  return items.map((item) => `${item.category || "General"} x ${Number(item.quantity) || 1}`).join(" · ");
}

function tierInventory(tier) {
  const soldByDate = tier.sold_by_date && typeof tier.sold_by_date === "object" ? tier.sold_by_date : null;
  const remainingByDate = tier.remaining_by_date && typeof tier.remaining_by_date === "object" ? tier.remaining_by_date : null;
  const dates = remainingByDate ? Object.keys(remainingByDate) : [];
  const perDateCapacity = Number(tier.capacity) || 0;
  return {
    sold: soldByDate ? Object.values(soldByDate).reduce((sum, value) => sum + (Number(value) || 0), 0) : Number(tier.sold) || 0,
    capacity: dates.length ? perDateCapacity * dates.length : perDateCapacity,
    remaining: remainingByDate ? Object.values(remainingByDate).reduce((sum, value) => sum + (Number(value) || 0), 0) : Number(tier.remaining) || 0,
  };
}

function initialDashboardTab() {
  if (typeof window === "undefined") return "stats";
  const tab = new URLSearchParams(window.location.search).get("tab");
  return DASHBOARD_TABS.includes(tab) ? tab : "stats";
}

// CSV helper
const toCsv = (rows, columns) => {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const head = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(",")).join("\n");
  return head + "\n" + body;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
};

export default function OrganizerDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { languages } = useApp();
  const [event, setEvent] = useState(null);
  const [loadState, setLoadState] = useState({ loading: true, error: "" });
  const [restricted, setRestricted] = useState("");
  const [bookings, setBookings] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [salesReport, setSalesReport] = useState(null);
  const [ads, setAds] = useState([]);
  const [scannerAccess, setScannerAccess] = useState({ pin: "", scanner_code: "", scanner_gates: [] });
  const [pin, setPin] = useState("");
  const [newGate, setNewGate] = useState("");
  const [gatesSaving, setGatesSaving] = useState(false);
  const [scannerAssignments, setScannerAssignments] = useState([]);
  const [assignmentDraft, setAssignmentDraft] = useState({ label: "", gates: [], expires_in_hours: 24 });
  const [createdAssignment, setCreatedAssignment] = useState(null);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const [boost, setBoost] = useState({ format: "card", languages: ["All"], duration_days: 7 });
  const [ticketingOpen, setTicketingOpen] = useState(false);
  const [ticketingChange, setTicketingChange] = useState({
    target_mode: "namaste",
    note: "",
    ticketing_approved: true,
    external_ticket_url: "",
    inquiry_phone: "",
    inquiry_whatsapp: "",
    inquiry_email: "",
  });
  const [refundTarget, setRefundTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState("");
  const [lastSync, setLastSync] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [activeTab, setActiveTab] = useState(initialDashboardTab);

  const fetchAll = async (silent = false) => {
    if (!eventId) return;
    if (!silent) setLoadState((current) => ({ ...current, loading: true }));
    try {
      const eventRes = await api.get(`/events/${eventId}`);
      setEvent(eventRes.data);

      const [b, c, r, a, s, sa] = await Promise.allSettled([
        api.get(`/organizer/events/${eventId}/bookings`),
        api.get(`/scanner/event/${eventId}/checkins`),
        api.get(`/events/${eventId}/sales-report`),
        api.get("/ads/my"),
        api.get(`/events/${eventId}/scanner-pin`),
        api.get(`/events/${eventId}/scanner-assignments`),
      ]);

      if (b.status === "fulfilled") setBookings(asArray(b.value.data));
      if (c.status === "fulfilled") setCheckins(asArray(c.value.data?.checkins ?? c.value.data));
      if (r.status === "fulfilled") setSalesReport(r.value.data || null);
      if (a.status === "fulfilled") setAds(asArray(a.value.data).filter((x) => x.event_id === eventId));
      if (s.status === "fulfilled") setScannerAccess({
        pin: s.value.data?.pin || "",
        scanner_code: s.value.data?.scanner_code || "",
        scanner_gates: asArray(s.value.data?.scanner_gates),
      });
      if (sa.status === "fulfilled") setScannerAssignments(asArray(sa.value.data?.assignments));

      const denied = [b, c, r, s].some((result) => result.status === "rejected" && result.reason?.response?.status === 403);
      setRestricted(denied ? "You can view this event, but organiser stats are restricted to the event owner or admin." : "");
      setLastSync(Date.now());
      setLoadState({ loading: false, error: "" });
    } catch (error) {
      const status = error?.response?.status;
      if (!silent) {
        setEvent(null);
        setLoadState({
          loading: false,
          error: status === 404 ? "Event not found." : "Could not load this event dashboard.",
        });
      }
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(() => fetchAll(true), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Handle return from Stripe boost checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("boost_session");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/ads/checkout/status/${sid}`);
        if (data.payment_status === "paid" && data.ad) {
          toast.success(`Boost activated — £${data.amount.toFixed(2)} charged`);
          // Strip the query param and refresh
          window.history.replaceState(null, "", window.location.pathname);
          fetchAll();
          return;
        }
        if (data.status === "expired") {
          toast.error("Boost session expired.");
          window.history.replaceState(null, "", window.location.pathname);
          return;
        }
        attempts += 1;
        if (attempts < 12) setTimeout(poll, 2000);
        else toast.message("Boost is processing — refresh the page in a minute.");
      } catch { /* keep trying */ }
    };
    toast.message("Confirming your boost payment…");
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastSync) / 1000)), 1000);
    return () => clearInterval(t);
  }, [lastSync]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    if (tab === "stats") url.searchParams.delete("tab");
    else url.searchParams.set("tab", tab);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  };

  if (loading || loadState.loading) return <div className="min-h-screen bg-background"><Navbar /></div>;
  if (!user) {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="page-shell py-10">
          <div className="rounded-2xl border border-border p-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h1 className="font-display text-2xl font-black">Sign in to open this dashboard</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">This organiser page has private bookings, check-ins and ad controls. Sign in as the organiser or admin to continue.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => navigate("/login", { state: { from: returnTo } })} className="rounded-full">Sign in</Button>
              <Button variant="outline" onClick={() => navigate(`/event/${eventId}`)} className="rounded-full">Open public event page</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="page-shell py-10">
          <div className="rounded-2xl border border-border p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <h1 className="font-display text-2xl font-black">Event dashboard unavailable</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{loadState.error || "This event could not be loaded."}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate("/profile")} className="rounded-full">Back to profile</Button>
              <Button onClick={() => navigate(`/event/${eventId}`)} className="rounded-full">Open public event page</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const capacity = Number(event.capacity ?? (event.prices || []).reduce((s, p) => s + (p.capacity || 0), 0));
  const sold = Number(event.tickets_sold ?? bookings
    .filter((booking) => !["cancelled", "refunded"].includes(booking.status))
    .reduce((sum, booking) => sum + (booking.quantity || 1), 0));
  const scannableTickets = bookings.reduce((total, booking) => {
    if (["cancelled", "refunded"].includes(booking.status)) return total;
    const qrUnits = asArray(booking.ticket_units).filter((unit) => unit.qr_code).length;
    return total + (qrUnits || (booking.qr_code ? (booking.quantity || 1) : 0));
  }, 0);
  const revenue = bookings.reduce((s, b) => s + (b.total || 0), 0);
  const progress = capacity ? Math.min(100, (sold / capacity) * 100) : 0;
  const entryRate = sold ? Math.round((checkins.length / sold) * 100) : 0;
  const scannerTicketingEnabled = event.status === "approved"
    && (event.ticketing_mode || "namaste") === "namaste"
    && event.ticketing_approved !== false;
  const scannerAvailable = scannerTicketingEnabled && scannableTickets > 0;

  const setEventPin = async () => {
    if (!pin) return;
    try {
      const { data } = await api.post("/scanner/event-pin", { event_id: eventId, pin });
      setScannerAccess((current) => ({ ...current, pin: data.pin || "", scanner_code: data.event_code || "" }));
      setScannerAssignments([]);
      setCreatedAssignment(null);
      toast.success(`Scanner credentials ready: ${data.event_code}`);
      setPin("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set PIN");
    }
  };

  const scannerCode = scannerAccess.scanner_code;
  const scannerPin = scannerAccess.pin;
  const scannerGates = asArray(scannerAccess.scanner_gates);
  const scannerInstructions = scannerCode
    ? `Namaste Network door scanner\nEvent: ${event.title}\nEvent Code: ${scannerCode}\nPIN: ${scannerPin || "your PIN"}\nGates: ${scannerGates.join(", ") || "Main Door"}\nOpen: ${window.location.origin}/scanner`
    : "";

  const copyScannerInstructions = async () => {
    if (!scannerInstructions) {
      toast.error("Generate scanner credentials first");
      return;
    }
    try {
      await navigator.clipboard.writeText(scannerInstructions);
      toast.success("Scanner instructions copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const saveScannerGates = async (gates) => {
    const cleaned = [...new Map(gates.map((value) => [value.trim().toLowerCase(), value.trim()])).values()].filter(Boolean).slice(0, 12);
    if (!cleaned.length) {
      toast.error("Add at least one gate");
      return;
    }
    setGatesSaving(true);
    try {
      const { data } = await api.put(`/events/${eventId}/scanner-gates`, { gates: cleaned });
      setScannerAccess((current) => ({ ...current, scanner_gates: data.scanner_gates || cleaned }));
      setNewGate("");
      toast.success("Scanner gates updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update gates");
    } finally {
      setGatesSaving(false);
    }
  };

  const addScannerGate = () => {
    const gate = newGate.trim();
    if (!gate) return;
    saveScannerGates([...scannerGates, gate]);
  };

  const toggleAssignmentGate = (gate) => {
    setAssignmentDraft((current) => ({
      ...current,
      gates: current.gates.includes(gate)
        ? current.gates.filter((item) => item !== gate)
        : [...current.gates, gate],
    }));
  };

  const createScannerAssignment = async () => {
    if (!assignmentDraft.label.trim() || !assignmentDraft.gates.length) return;
    setAssignmentBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/scanner-assignments`, {
        ...assignmentDraft,
        label: assignmentDraft.label.trim(),
      });
      setCreatedAssignment(data);
      setAssignmentDraft({ label: "", gates: [], expires_in_hours: 24 });
      const list = await api.get(`/events/${eventId}/scanner-assignments`);
      setScannerAssignments(asArray(list.data?.assignments));
      toast.success("Staff scanner access created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create staff scanner access");
    } finally {
      setAssignmentBusy(false);
    }
  };

  const revokeScannerAssignment = async (assignmentId) => {
    setAssignmentBusy(true);
    try {
      await api.delete(`/events/${eventId}/scanner-assignments/${assignmentId}`);
      setScannerAssignments((current) => current.filter((item) => item.assignment_id !== assignmentId));
      toast.success("Staff scanner access revoked");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not revoke scanner access");
    } finally {
      setAssignmentBusy(false);
    }
  };

  const copyAssignment = async (assignment) => {
    const note = `Namaste Scanner assignment\nEvent: ${event.title}\nTask: ${assignment.label}\nEvent Code: ${assignment.event_code}\nStaff PIN: ${assignment.pin}\nGate${assignment.gates.length === 1 ? "" : "s"}: ${assignment.gates.join(", ")}\nOpen: ${window.location.origin}/scanner`;
    try {
      await navigator.clipboard.writeText(note);
      toast.success("Staff scanner assignment copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const submitBoost = async () => {
    try {
      const { data } = await api.post("/ads/checkout/session", {
        event_id: eventId,
        format: boost.format,
        languages: boost.languages,
        duration_days: boost.duration_days,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Boost failed");
    }
  };

  const openTicketingChange = () => {
    const current = event.ticketing_mode || "namaste";
    const nextMode = current === "namaste" ? "inquiry" : "namaste";
    setTicketingChange({
      target_mode: nextMode,
      note: "",
      ticketing_approved: nextMode === "namaste",
      external_ticket_url: event.external_ticket_url || "",
      inquiry_phone: event.inquiry_phone || "",
      inquiry_whatsapp: event.inquiry_whatsapp || "",
      inquiry_email: event.inquiry_email || "",
    });
    setTicketingOpen(true);
  };

  const submitTicketingChange = async () => {
    try {
      await api.post(`/events/${eventId}/ticketing-change-request`, ticketingChange);
      toast.success("Ticketing change sent for admin approval");
      setTicketingOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request ticketing change");
    }
  };

  const refundBooking = async () => {
    if (!refundTarget) return;
    setActionBusy(`refund-${refundTarget.booking_id}`);
    try {
      const { data } = await api.post(`/organizer/bookings/${refundTarget.booking_id}/refund`, {});
      setBookings((rows) => rows.map((b) => b.booking_id === data.booking.booking_id ? data.booking : b));
      toast.success(`Refunded £${((data.refund?.amount || 0) / 100).toFixed(2)}`);
      setRefundTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setActionBusy("");
    }
  };

  const resendTicket = async (booking) => {
    setActionBusy(`resend-${booking.booking_id}`);
    try {
      await api.post(`/organizer/bookings/${booking.booking_id}/resend-ticket`);
      toast.success(`Ticket resent to ${booking.user_name || booking.user_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend ticket");
    } finally {
      setActionBusy("");
    }
  };

  const copyBookingId = async (bookingId) => {
    try {
      await navigator.clipboard.writeText(bookingId);
      toast.success("Booking ID copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const totalImpressions = ads.reduce((s, a) => s + (a.impressions || 0), 0);
  const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
  const ctr = totalImpressions ? ((totalClicks / totalImpressions) * 100).toFixed(2) : "0.00";
  const languageOptions = asArray(languages);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="page-shell py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-black text-primary">Organizer</p>
            <h1 className="font-display text-3xl md:text-4xl font-black mt-1">{event.title}</h1>
            <p className="text-xs text-muted-foreground">{event.venue} · {event.date} {event.time}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate("/organiser-guide")}>
              <BookOpen className="mr-2 h-4 w-4" /> Guide
            </Button>
            <span className="inline-flex items-center gap-2 text-xs font-bold text-destructive" data-testid="live-status-pill">
              <span className="relative inline-block h-2 w-2 rounded-full bg-destructive">
                <span className="absolute inset-0 rounded-full bg-destructive animate-ping opacity-75" />
              </span>
              LIVE · synced {secondsAgo}s ago
            </span>
          </div>
        </div>
        {restricted && (
          <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">
            {restricted}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={selectTab} className="mt-6">
          <TabsList className="grid grid-cols-5 max-w-3xl">
            <TabsTrigger value="stats" data-testid="tab-stats">Stats</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="checkins" data-testid="tab-checkins">Check-ins</TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-ads">Ad Stats</TabsTrigger>
          </TabsList>

          <TabsContent value="stats" className="mt-6 grid md:grid-cols-4 gap-3">
            <StatCard label="Revenue" value={`£${revenue.toFixed(2)}`} icon={TrendingUp} />
            <StatCard label="Tickets sold" value={sold} icon={Users} />
            <StatCard label="Capacity" value={capacity} icon={Sparkles} />
            <StatCard label="Entry rate" value={`${entryRate}%`} icon={ScanLine} />
            <div className="md:col-span-4 rounded-2xl border border-border p-5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold">Capacity utilisation</span>
                <span className="font-mono">{sold}/{capacity}</span>
              </div>
              <Progress value={progress} className="mt-3" data-testid="capacity-progress" />
            </div>
            <div className="md:col-span-4 rounded-2xl border border-border p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] font-black text-primary flex items-center gap-2">
                    <Receipt className="h-4 w-4" /> Ticketing mode
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Current mode: <span className="font-bold text-foreground">{ticketingLabels[event.ticketing_mode || "namaste"]}</span>
                    {event.ticketing_mode === "namaste" && event.ticketing_approved === false ? " · checkout pending admin enablement" : ""}
                  </p>
                  {event.ticketing_change_request?.status === "pending" ? (
                    <p className="mt-2 text-sm text-amber-700">
                      Pending admin approval: {ticketingLabels[event.ticketing_change_request.current_mode]} → {ticketingLabels[event.ticketing_change_request.target_mode]}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Request a change after publishing. The live event keeps its current mode until admin approves.
                    </p>
                  )}
                </div>
                <Button variant="outline" className="rounded-full" onClick={openTicketingChange} disabled={event.ticketing_change_request?.status === "pending"}>
                  Request mode change
                </Button>
              </div>
              {event.ticketing_mode === "namaste" && sold > 0 && (
                <p className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-xs text-amber-700">
                  This event already has {sold} sold ticket{sold === 1 ? "" : "s"}. Existing QR tickets must remain valid; admin cannot disable Namaste ticketing until bookings are resolved.
                </p>
              )}
            </div>
            <div className="md:col-span-4 rounded-2xl border border-border p-5">
              {!scannerAvailable ? (
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] font-black text-primary flex items-center gap-2">
                    <KeyRound className="h-4 w-4" /> Door Scanner
                  </p>
                  <p className="mt-2 text-sm font-bold">
                    {scannerTicketingEnabled ? "Scanner activates after the first ticket sale" : "Door scanner is not required for this event"}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {scannerTicketingEnabled
                      ? "No active QR tickets have been generated yet. The Event Code and PIN controls will appear after the first Namaste ticket is sold."
                      : "Only approved Namaste ticketing events generate QR tickets. Inquiry and external-ticket events do not need Namaste scanner access."}
                  </p>
                </div>
              ) : (
                <>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] font-black text-primary flex items-center gap-2">
                    <KeyRound className="h-4 w-4" /> Door Scanner Access
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Generate a private event code and PIN for door staff. Resetting the PIN also rotates the event code.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="rounded-full" onClick={() => navigate(`/scanner?event=${eventId}`)}>
                    <ExternalLink className="h-4 w-4 mr-2" /> Open scanner
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={copyScannerInstructions} disabled={!scannerCode}>
                    <Copy className="h-4 w-4 mr-2" /> Copy scanner note
                  </Button>
                </div>
              </div>
              {scannerCode ? (
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <CredentialBox label="Event code" value={scannerCode} />
                  <CredentialBox label="PIN status" value={scannerPin ? "PIN saved" : "No PIN stored"} dim />
                  <CredentialBox label="Scanner URL" value={`${window.location.origin}/scanner`} small />
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                  No event scanner credentials yet. Set a PIN below before sharing scanner access with staff.
                </div>
              )}
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  data-testid="event-pin-input"
                  placeholder={scannerCode ? "New PIN to reset credentials" : "Set PIN, min 4 digits"}
                  value={pin}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="max-w-[16rem]"
                />
                <Button onClick={setEventPin} disabled={!/^\d{4,8}$/.test(pin)} className="rounded-full" data-testid="set-event-pin">
                  {scannerCode ? "Reset code + PIN" : "Generate code + PIN"}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Door staff enter the Event Code and PIN, then choose one of the gates you configure below.
              </p>
              <div className="mt-5 border-t border-border pt-4">
                <p className="text-sm font-bold">Event gates</p>
                <p className="mt-1 text-xs text-muted-foreground">These are the only gate choices shown in the scanner.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {scannerGates.map((gate) => (
                    <button
                      key={gate}
                      type="button"
                      className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs font-bold"
                      onClick={() => saveScannerGates(scannerGates.filter((item) => item !== gate))}
                      disabled={gatesSaving || scannerGates.length === 1}
                      title={scannerGates.length === 1 ? "At least one gate is required" : `Remove ${gate}`}
                    >
                      {gate} ×
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex max-w-md gap-2">
                  <Input
                    value={newGate}
                    onChange={(e) => setNewGate(e.target.value.slice(0, 40))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addScannerGate(); } }}
                    placeholder="e.g. North Entrance"
                    data-testid="scanner-gate-input"
                  />
                  <Button type="button" variant="outline" onClick={addScannerGate} disabled={!newGate.trim() || gatesSaving || scannerGates.length >= 12}>
                    Add gate
                  </Button>
                </div>
              </div>
              {scannerCode && (
                <div className="mt-5 border-t border-border pt-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-lg bg-primary/10 p-2 text-primary"><UserPlus className="h-4 w-4" /></div>
                    <div>
                      <p className="text-sm font-bold">Staff scanner assignments</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Create separate, temporary access for each scanner device or door task. Staff can scan and view entry totals only; they cannot edit the event, tickets, payments or settings.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 rounded-xl border border-border bg-muted/20 p-4">
                    <Input
                      value={assignmentDraft.label}
                      onChange={(e) => setAssignmentDraft((current) => ({ ...current, label: e.target.value.slice(0, 60) }))}
                      placeholder="Task label, e.g. North Gate scanner 1"
                      data-testid="scanner-assignment-label"
                    />
                    <div>
                      <p className="text-xs font-bold">Allowed gates</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {scannerGates.map((gate) => {
                          const selected = assignmentDraft.gates.includes(gate);
                          return (
                            <button
                              key={gate}
                              type="button"
                              onClick={() => toggleAssignmentGate(gate)}
                              className={`rounded-full border px-3 py-1.5 text-xs font-bold ${selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground"}`}
                            >
                              {gate}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Select
                        value={String(assignmentDraft.expires_in_hours)}
                        onValueChange={(value) => setAssignmentDraft((current) => ({ ...current, expires_in_hours: Number(value) }))}
                      >
                        <SelectTrigger className="sm:w-[12rem]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="8">Expires in 8 hours</SelectItem>
                          <SelectItem value="12">Expires in 12 hours</SelectItem>
                          <SelectItem value="24">Expires in 24 hours</SelectItem>
                          <SelectItem value="48">Expires in 2 days</SelectItem>
                          <SelectItem value="168">Expires in 7 days</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        onClick={createScannerAssignment}
                        disabled={assignmentBusy || !assignmentDraft.label.trim() || !assignmentDraft.gates.length}
                      >
                        <UserPlus className="mr-2 h-4 w-4" /> Create staff access
                      </Button>
                    </div>
                  </div>

                  {createdAssignment && (
                    <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                      <p className="text-xs font-black uppercase text-emerald-700">Share these details now</p>
                      <p className="mt-1 text-xs text-muted-foreground">The unique PIN is shown only when the assignment is created.</p>
                      <div className="mt-3 grid gap-2 sm:grid-cols-3">
                        <CredentialBox label="Task" value={createdAssignment.label} />
                        <CredentialBox label="Event code" value={createdAssignment.event_code} />
                        <CredentialBox label="Staff PIN" value={createdAssignment.pin} />
                      </div>
                      <Button type="button" variant="outline" className="mt-3" onClick={() => copyAssignment(createdAssignment)}>
                        <Copy className="mr-2 h-4 w-4" /> Copy staff instructions
                      </Button>
                    </div>
                  )}

                  <div className="mt-3 space-y-2">
                    {scannerAssignments.map((assignment) => (
                      <div key={assignment.assignment_id} className="flex flex-col gap-2 rounded-xl border border-border p-3 sm:flex-row sm:items-center">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold">{assignment.label}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {asArray(assignment.gates).join(", ")} · expires {new Date(assignment.expires_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={assignmentBusy}
                          onClick={() => revokeScannerAssignment(assignment.assignment_id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Revoke
                        </Button>
                      </div>
                    ))}
                    {!scannerAssignments.length && <p className="text-xs text-muted-foreground">No active staff assignments.</p>}
                  </div>
                </div>
              )}
                </>
              )}
            </div>
            <div className="md:col-span-4">
              <Dialog open={boostOpen} onOpenChange={setBoostOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="boost-event-btn" className="rounded-full">
                    <Megaphone className="h-4 w-4 mr-2" /> Boost This Event
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Boost as Featured Ad</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-bold">Format</label>
                      <Select value={boost.format} onValueChange={(v) => setBoost({ ...boost, format: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hero">Hero Banner</SelectItem>
                          <SelectItem value="card">Also Featured Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-bold">Target language</label>
                      <Select value={boost.languages[0]} onValueChange={(v) => setBoost({ ...boost, languages: [v] })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="All">All</SelectItem>
                          {languageOptions.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-bold">Duration</label>
                      <Select value={String(boost.duration_days)} onValueChange={(v) => setBoost({ ...boost, duration_days: +v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">1 week — £25</SelectItem>
                          <SelectItem value="14">2 weeks — £45</SelectItem>
                          <SelectItem value="30">1 month — £80</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={submitBoost} className="rounded-full" data-testid="boost-submit">
                      Pay £{ {7: 25, 14: 45, 30: 80}[boost.duration_days] || 25} via Stripe →
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6 space-y-3">
            <BookingsHeader event={event} bookings={bookings} />
            <BookingsTable
              bookings={bookings}
              actionBusy={actionBusy}
              onCopyBooking={copyBookingId}
              onExport={() => {
                const cols = [
                  { label: "Booking ID",   get: (r) => r.booking_id },
                  { label: "Name",         get: (r) => r.user_name },
                  { label: "Email",        get: (r) => r.user_email },
                  { label: "Tier",         get: (r) => r.category || "" },
                  { label: "Quantity",     get: (r) => r.quantity },
                  { label: "Total (£)",    get: (r) => (r.total ?? 0).toFixed(2) },
                  { label: "Refunded (£)", get: (r) => (r.refunded_amount ?? 0).toFixed(2) },
                  { label: "Status",       get: (r) => r.status },
                  { label: "Payment",      get: (r) => r.payment_method },
                  { label: "Booked at",    get: (r) => r.created_at },
                  { label: "Scanned at",   get: (r) => r.scanned_at || "" },
                ];
                downloadCsv(`bookings-${event.event_id}.csv`, toCsv(bookings, cols));
                toast.success(`Exported ${bookings.length} bookings`);
              }}
              onRefund={(booking) => setRefundTarget(booking)}
              onResend={resendTicket}
            />
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <SalesReportPanel report={salesReport} event={event} onExport={(name, rows, cols) => {
              downloadCsv(`${name}-${event.event_id}.csv`, toCsv(rows, cols));
              toast.success(`Exported ${name}`);
            }} />
          </TabsContent>

          <TabsContent value="checkins" className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">{checkins.length}</span> check-ins · entry rate{" "}
                <span className="font-bold text-foreground">{entryRate}%</span>
              </p>
              <Button
                size="sm"
                variant="outline"
                data-testid="export-checkins-csv"
                disabled={checkins.length === 0}
                onClick={() => {
                  const cols = [
                    { label: "Name",        get: (r) => r.user_name },
                    { label: "Email",       get: (r) => r.user_email },
                    { label: "Tier",        get: (r) => r.category || "" },
                    { label: "Booking ID",  get: (r) => r.booking_id },
                    { label: "Ticket ID",   get: (r) => r.ticket_id || "" },
                    { label: "Gate",        get: (r) => r.entry_point || "" },
                    { label: "Scanned at",  get: (r) => r.scanned_at },
                  ];
                  downloadCsv(`checkins-${event.event_id}.csv`, toCsv(checkins, cols));
                  toast.success(`Exported ${checkins.length} check-ins`);
                }}
                className="rounded-full"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>
            <div className="rounded-2xl border border-border overflow-hidden">
              <table className="w-full text-sm" data-testid="checkins-table">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="p-3">Name</th><th className="p-3">Tier</th><th className="p-3">Gate</th><th className="p-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.length === 0 ? (
                    <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No check-ins yet.</td></tr>
                  ) : checkins.map((c) => (
                    <tr key={c.checkin_id} className="border-t border-border">
                      <td className="p-3">
                        <p className="font-bold">{c.user_name}</p>
                        <p className="text-xs text-muted-foreground">{c.user_email}</p>
                      </td>
                      <td className="p-3">{c.category || "—"}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        <p>{c.entry_point || "Main Door"}</p>
                      </td>
                      <td className="p-3 font-mono text-xs">{new Date(c.scanned_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="ads" className="mt-6 grid md:grid-cols-3 gap-3">
            <StatCard label="Impressions" value={totalImpressions} icon={TrendingUp} />
            <StatCard label="Clicks" value={totalClicks} icon={Users} />
            <StatCard label="CTR" value={`${ctr}%`} icon={Sparkles} />
            <div className="md:col-span-3 rounded-2xl border border-border p-5">
              <p className="text-xs uppercase tracking-[0.25em] font-black text-primary mb-3">Per-ad breakdown</p>
              {ads.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active boosts. Click "Boost This Event" to promote.</p>
              ) : (
                <div className="space-y-2">
                  {ads.map((a) => {
                    const days = Math.max(0, Math.ceil((new Date(a.end_date) - new Date()) / 86400000));
                    return (
                      <div key={a.ad_id} className="grid grid-cols-5 gap-3 p-3 rounded-xl border border-border text-sm">
                        <div><p className="text-xs uppercase font-bold text-muted-foreground">Format</p>{a.format}</div>
                        <div><p className="text-xs uppercase font-bold text-muted-foreground">Languages</p>{a.languages.join(", ")}</div>
                        <div><p className="text-xs uppercase font-bold text-muted-foreground">Impressions</p>{a.impressions}</div>
                        <div><p className="text-xs uppercase font-bold text-muted-foreground">Clicks</p>{a.clicks}</div>
                        <div><p className="text-xs uppercase font-bold text-muted-foreground">Days left</p>{days}d</div>
                      </div>
                    );
                  })}
                  <Button variant="outline" onClick={() => setBoostOpen(true)} className="rounded-full" data-testid="boost-again-btn">
                    Boost again
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <Dialog open={ticketingOpen} onOpenChange={setTicketingOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request ticketing mode change</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold">New mode</label>
              <Select
                value={ticketingChange.target_mode}
                onValueChange={(value) => setTicketingChange((current) => ({
                  ...current,
                  target_mode: value,
                  ticketing_approved: value === "namaste",
                }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="namaste">Namaste ticketing</SelectItem>
                  <SelectItem value="external">External ticket link</SelectItem>
                  <SelectItem value="inquiry">Inquiry only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ticketingChange.target_mode === "namaste" && (
              <label className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={Boolean(ticketingChange.ticketing_approved)}
                  onChange={(event) => setTicketingChange((current) => ({ ...current, ticketing_approved: event.target.checked }))}
                />
                Request checkout to be enabled
              </label>
            )}
            {ticketingChange.target_mode === "external" && (
              <Input
                value={ticketingChange.external_ticket_url}
                onChange={(event) => setTicketingChange((current) => ({ ...current, external_ticket_url: event.target.value }))}
                placeholder="https://organiser-ticket-link.example"
              />
            )}
            {ticketingChange.target_mode === "inquiry" && (
              <div className="grid gap-2 sm:grid-cols-3">
                <Input value={ticketingChange.inquiry_phone} onChange={(event) => setTicketingChange((current) => ({ ...current, inquiry_phone: event.target.value }))} placeholder="Phone" />
                <Input value={ticketingChange.inquiry_whatsapp} onChange={(event) => setTicketingChange((current) => ({ ...current, inquiry_whatsapp: event.target.value }))} placeholder="WhatsApp" />
                <Input value={ticketingChange.inquiry_email} onChange={(event) => setTicketingChange((current) => ({ ...current, inquiry_email: event.target.value }))} placeholder="Email" />
              </div>
            )}
            <Input
              value={ticketingChange.note}
              onChange={(event) => setTicketingChange((current) => ({ ...current, note: event.target.value }))}
              placeholder="Reason for admin, e.g. organiser now wants Namaste ticket sales"
            />
            <p className="text-xs text-muted-foreground">
              Admin will review the change. Current ticketing remains live until approval.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketingOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitTicketingChange} className="rounded-full">Send for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!refundTarget} onOpenChange={(open) => !open && setRefundTarget(null)}>
        <AlertDialogContent data-testid="organizer-refund-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Refund this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This refunds the remaining balance for {refundTarget?.user_name || "this attendee"} and invalidates the ticket immediately.
              Scanned tickets and already refunded tickets cannot be refunded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!actionBusy}>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={refundBooking}
              disabled={!!actionBusy}
              data-testid="organizer-refund-confirm-btn"
            >
              {actionBusy ? "Processing..." : "Refund ticket"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-border p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.25em] font-black text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <p className="font-display text-3xl font-black mt-2">{value}</p>
    </div>
  );
}

function CredentialBox({ label, value, dim = false, small = false }) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`mt-1 break-all font-mono font-black ${small ? "text-xs" : "text-lg"} ${dim ? "text-muted-foreground" : "text-foreground"}`}>
        {value || "-"}
      </p>
    </div>
  );
}

function SalesReportPanel({ report, event, onExport }) {
  if (!report) {
    return (
      <div className="rounded-2xl border border-border p-6 text-sm text-muted-foreground">
        Sales reporting is not available for this event yet.
      </div>
    );
  }
  const summary = report.summary || {};
  const settlement = report.settlement || {};
  const daily = asArray(report.daily_sales);
  const byTicket = asArray(report.by_ticket);
  const byValidDay = asArray(report.by_valid_day);
  const gateRows = asArray(report.checkins?.by_gate);
  const money = (value) => `£${Number(value || 0).toFixed(2)}`;
  const dailyColumns = [
    { label: "Date", get: (r) => r.date },
    { label: "Bookings", get: (r) => r.bookings },
    { label: "Tickets", get: (r) => r.tickets },
    { label: "Gross Sales", get: (r) => Number(r.gross_sales || 0).toFixed(2) },
    { label: "Refunds", get: (r) => Number(r.refunds || 0).toFixed(2) },
    { label: "Net Sales", get: (r) => Number(r.net_sales || 0).toFixed(2) },
    { label: "Check-ins", get: (r) => r.checkins },
  ];
  const ticketColumns = [
    { label: "Ticket Type", get: (r) => r.name },
    { label: "Tickets", get: (r) => r.tickets },
    { label: "Gross Sales", get: (r) => Number(r.gross_sales || 0).toFixed(2) },
    { label: "Refunds", get: (r) => Number(r.refunds || 0).toFixed(2) },
    { label: "Net Sales", get: (r) => Number(r.net_sales || 0).toFixed(2) },
  ];
  const settlementRows = [{
    event_id: event.event_id,
    event_title: event.title,
    gross_sales: summary.gross_sales,
    refunds: summary.refunds,
    net_sales: summary.net_sales,
    platform_fee_pct: summary.platform_fee_pct,
    platform_fee_fixed_pence: summary.platform_fee_fixed_pence,
    platform_fee_estimate: summary.platform_fee_estimate,
    organizer_due_estimate: summary.organizer_due_estimate,
    settlement_status: settlement.status,
    connected_account_id: settlement.connected_account_id,
    generated_at: report.generated_at,
  }];
  const settlementColumns = [
    { label: "Event ID", get: (r) => r.event_id },
    { label: "Event", get: (r) => r.event_title },
    { label: "Gross Sales", get: (r) => Number(r.gross_sales || 0).toFixed(2) },
    { label: "Refunds", get: (r) => Number(r.refunds || 0).toFixed(2) },
    { label: "Net Sales", get: (r) => Number(r.net_sales || 0).toFixed(2) },
    { label: "Platform Fee %", get: (r) => r.platform_fee_pct },
    { label: "Fixed Fee", get: (r) => (Number(r.platform_fee_fixed_pence || 0) / 100).toFixed(2) },
    { label: "Platform Fee Estimate", get: (r) => Number(r.platform_fee_estimate || 0).toFixed(2) },
    { label: "Organizer Due Estimate", get: (r) => Number(r.organizer_due_estimate || 0).toFixed(2) },
    { label: "Settlement Status", get: (r) => r.settlement_status },
    { label: "Connected Account", get: (r) => r.connected_account_id },
    { label: "Generated At", get: (r) => r.generated_at },
  ];

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        <StatCard label="Gross sales" value={money(summary.gross_sales)} icon={TrendingUp} />
        <StatCard label="Refunds" value={money(summary.refunds)} icon={RefreshCcw} />
        <StatCard label="Net sales" value={money(summary.net_sales)} icon={Receipt} />
        <StatCard label="Due estimate" value={money(summary.organizer_due_estimate)} icon={FileText} />
      </div>

      <div className="rounded-2xl border border-border p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Settlement</p>
            <h3 className="font-display text-xl font-black mt-1">
              {settlement.connected_account_id ? "Automatic Stripe Connect payout" : settlement.manual_settlement_required ? "Namaste collection · manual settlement" : "Namaste platform collection"}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{settlement.note}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => onExport("settlement-report", settlementRows, settlementColumns)}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export settlement
          </Button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <CredentialBox label="Status" value={settlement.status || "pending"} />
          <CredentialBox label="Connected account" value={settlement.connected_account_id || "Not connected"} small dim={!settlement.connected_account_id} />
          <CredentialBox label="Completed payments" value={`${settlement.completed_transactions || 0}/${settlement.transactions || 0}`} />
          <CredentialBox label="Buyer booking fee" value={`${Number(summary.platform_fee_pct || 0).toFixed(1)}% + ${money(Number(summary.platform_fee_fixed_pence || 0) / 100)}`} />
        </div>
      </div>

      <ReportTable
        title="Daily ticket sales"
        rows={daily}
        columns={[
          ["Date", (r) => r.date],
          ["Bookings", (r) => r.bookings],
          ["Tickets", (r) => r.tickets],
          ["Gross", (r) => money(r.gross_sales)],
          ["Refunds", (r) => money(r.refunds)],
          ["Net", (r) => money(r.net_sales)],
          ["Check-ins", (r) => r.checkins],
        ]}
        onExport={() => onExport("daily-sales", daily, dailyColumns)}
      />

      <div className="grid gap-4">
        <ReportTable
          title="By ticket type"
          rows={byTicket}
          columns={[
            ["Ticket", (r) => r.name],
            ["Tickets", (r) => r.tickets],
            ["Gross", (r) => money(r.gross_sales)],
            ["Net", (r) => money(r.net_sales)],
          ]}
          onExport={() => onExport("ticket-type-sales", byTicket, ticketColumns)}
        />
        <ReportTable
          title="By valid event day"
          rows={byValidDay}
          columns={[
            ["Valid day", (r) => r.date],
            ["Tickets", (r) => r.tickets],
            ["Gross", (r) => money(r.gross_sales)],
          ]}
          onExport={() => onExport("valid-day-sales", byValidDay, [
            { label: "Valid Day", get: (r) => r.date },
            { label: "Tickets", get: (r) => r.tickets },
            { label: "Gross Sales", get: (r) => Number(r.gross_sales || 0).toFixed(2) },
          ])}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ReportTable
          title="Check-ins by gate"
          rows={gateRows}
          columns={[["Gate", (r) => r.name], ["Tickets", (r) => r.tickets]]}
          onExport={() => onExport("checkins-by-gate", gateRows, [
            { label: "Gate", get: (r) => r.name },
            { label: "Tickets", get: (r) => r.tickets },
          ])}
        />
      </div>
    </div>
  );
}

function ReportTable({ title, rows, columns, onExport }) {
  return (
    <div className="rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border bg-muted/30">
        <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">{title}</p>
        <Button size="sm" variant="outline" className="rounded-full" disabled={!rows.length} onClick={onExport}>
          <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              {columns.map(([label]) => <th key={label} className="p-3">{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="p-6 text-center text-muted-foreground">No data yet.</td></tr>
            ) : rows.map((row, idx) => (
              <tr key={`${title}-${idx}`} className="border-t border-border">
                {columns.map(([label, get]) => <td key={label} className="p-3">{get(row)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingsHeader({ event, bookings }) {
  const active = bookings.filter((booking) => ACTIVE_BOOKING_STATUSES.includes(booking.status));
  const total = active.reduce((sum, booking) => sum + (Number(booking.total) || 0) - (Number(booking.refunded_amount) || 0), 0);
  const qty = active.reduce((sum, booking) => sum + bookingQuantity(booking), 0);
  const capacity = Number(event.capacity) || 0;
  const remaining = Number.isFinite(Number(event.tickets_remaining)) ? Number(event.tickets_remaining) : Math.max(0, capacity - qty);

  return (
    <div className="space-y-3" data-testid="bookings-summary">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-border p-4">
          <p className="text-[11px] uppercase tracking-[0.25em] font-black text-muted-foreground">Bookings</p>
          <p className="font-display text-2xl font-black mt-1">{active.length}</p>
          <p className="text-xs text-muted-foreground">{qty} active tickets</p>
        </div>
        <div className="rounded-2xl border border-border p-4">
          <p className="text-[11px] uppercase tracking-[0.25em] font-black text-muted-foreground">Revenue</p>
          <p className="font-display text-2xl font-black mt-1">£{total.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">avg £{active.length ? (total / active.length).toFixed(2) : "0.00"} / booking</p>
        </div>
        <div className="rounded-2xl border border-border p-4">
          <p className="text-[11px] uppercase tracking-[0.25em] font-black text-muted-foreground">Capacity</p>
          <p className="font-display text-2xl font-black mt-1">{capacity || "Unlimited"}</p>
          <p className="text-xs text-muted-foreground">event ticket allocation</p>
        </div>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
          <p className="text-[11px] uppercase tracking-[0.25em] font-black text-primary">Remaining</p>
          <p className="font-display text-2xl font-black mt-1">{capacity ? remaining : "Unlimited"}</p>
          <p className="text-xs text-muted-foreground">available to book</p>
        </div>
      </div>
      {asArray(event.prices).length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[620px] text-sm" data-testid="organizer-tier-inventory">
            <thead className="bg-muted/50 text-left">
              <tr><th className="p-3">Ticket type</th><th className="p-3">Sold</th><th className="p-3">Capacity</th><th className="p-3">Remaining</th></tr>
            </thead>
            <tbody>{asArray(event.prices).map((tier, index) => {
              const inventory = tierInventory(tier);
              return (
                <tr key={tier.ticket_key || `${tier.tier}-${index}`} className="border-t border-border">
                  <td className="p-3 font-bold">{tier.tier || `Tier ${index + 1}`}</td>
                  <td className="p-3">{inventory.sold}</td>
                  <td className="p-3">{inventory.capacity || "Unlimited"}</td>
                  <td className="p-3 font-bold text-primary">{inventory.capacity ? inventory.remaining : "Unlimited"}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  confirmed: "bg-primary/10 text-primary",
  scanned: "bg-destructive/10 text-destructive",
  partially_scanned: "bg-sky-500/10 text-sky-600",
  cancelled: "bg-muted text-muted-foreground",
  refunded: "bg-muted text-muted-foreground",
  partially_refunded: "bg-orange-500/10 text-orange-600",
};

const ACTIVE_STATUSES = ACTIVE_BOOKING_STATUSES;

function BookingsTable({ bookings, actionBusy, onCopyBooking, onExport, onRefund, onResend }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const filtered = bookings.filter((b) => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [b.user_name, b.user_email, b.booking_id, b.category, bookingTicketLabel(b)]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
  });
  const canRefund = (b) => ACTIVE_STATUSES.includes(b.status);
  const canResend = (b) => !["cancelled", "refunded"].includes(b.status);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          data-testid="bookings-search"
          placeholder="Search name, email, ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex items-center gap-1 ml-1" data-testid="bookings-status-filter">
          {["all", "confirmed", "scanned", "partially_scanned", "partially_refunded", "refunded", "cancelled"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`pill text-xs ${statusFilter === s ? "pill-active" : "hover:bg-muted"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{filtered.length} of {bookings.length}</span>
          <Button
            size="sm"
            variant="outline"
            data-testid="export-bookings-csv"
            disabled={bookings.length === 0}
            onClick={onExport}
            className="rounded-full"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-border overflow-x-auto">
        <table className="w-full min-w-[1080px] text-sm" data-testid="bookings-table">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">Attendee</th>
              <th className="p-3">Tier</th>
              <th className="p-3">Qty</th>
              <th className="p-3">Total</th>
              <th className="p-3">Refunded</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Status</th>
              <th className="p-3">Booked</th>
              <th className="p-3">Booking ID</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                <Receipt className="h-6 w-6 mx-auto mb-2 opacity-50" />
                No bookings match this filter.
              </td></tr>
            ) : filtered.map((b) => (
              <tr key={b.booking_id} className="border-t border-border" data-testid={`booking-row-${b.booking_id}`}>
                <td className="p-3">
                  <p className="font-bold">{b.user_name}</p>
                  <p className="text-xs text-muted-foreground">{b.user_email}</p>
                </td>
                <td className="p-3 max-w-[240px] text-xs">{bookingTicketLabel(b)}</td>
                <td className="p-3 font-mono font-bold">{bookingQuantity(b)}</td>
                <td className="p-3 font-bold">£{(b.total || 0).toFixed(2)}</td>
                <td className="p-3 font-mono text-xs">£{(b.refunded_amount || 0).toFixed(2)}</td>
                <td className="p-3 text-xs">{b.payment_method}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full ${STATUS_STYLE[b.status] || STATUS_STYLE.confirmed}`}>
                    {b.status === "scanned" && <ShieldCheck className="h-3 w-3" />}
                    {b.status}
                  </span>
                </td>
                <td className="p-3 font-mono text-[11px]">{new Date(b.created_at).toLocaleString()}</td>
                <td className="p-3">
                  <button
                    type="button"
                    onClick={() => onCopyBooking(b.booking_id)}
                    className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground"
                    data-testid={`copy-booking-${b.booking_id}`}
                  >
                    {b.booking_id}
                    <Copy className="h-3 w-3" />
                  </button>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canResend(b) || actionBusy === `resend-${b.booking_id}`}
                      onClick={() => onResend(b)}
                      className="h-8 rounded-full px-2"
                      data-testid={`resend-ticket-${b.booking_id}`}
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!canRefund(b) || actionBusy === `refund-${b.booking_id}`}
                      onClick={() => onRefund(b)}
                      className="h-8 rounded-full px-2 text-destructive hover:text-destructive"
                      data-testid={`organizer-refund-${b.booking_id}`}
                    >
                      <RefreshCcw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
